import React, { useState } from 'react';
import { Mic, MicOff, MessageSquare, Video, X, Loader } from 'lucide-react';

export type SessionMode = 'chat-only' | 'meet-voice-chat' | 'mic-voice-chat';

interface PythonAudioControlsProps {
  isSupported: boolean;
  isSessionActive: boolean;
  onStartSession: (mode: SessionMode) => void;
  onStopSession: () => void;
}

export const PythonAudioControls: React.FC<PythonAudioControlsProps> = ({
  isSupported,
  isSessionActive,
  onStartSession,
  onStopSession
}) => {
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  const handleStartClick = () => {
    setShowModeSelector(true);
  };

  const handleModeSelect = async (mode: SessionMode) => {
    setShowModeSelector(false);
    setIsStarting(true);
    try {
      await onStartSession(mode);
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopClick = async () => {
    setIsStopping(true);
    try {
      await onStopSession();
    } finally {
      setIsStopping(false);
    }
  };

  const handleClose = () => {
    setShowModeSelector(false);
  };

  return (
    <>
      {/* Loading Overlay for Start Session */}
      {isStarting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4">
            <Loader className="w-8 h-8 text-primary-600 animate-spin" />
            <p className="text-gray-900 font-medium">Starting interview session...</p>
            <p className="text-sm text-gray-500">Setting up audio and connections</p>
          </div>
        </div>
      )}

      {/* Loading Overlay for Stop Session */}
      {isStopping && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4">
            <Loader className="w-8 h-8 text-red-600 animate-spin" />
            <p className="text-gray-900 font-medium">Ending interview session...</p>
            <p className="text-sm text-gray-500">Saving session data</p>
          </div>
        </div>
      )}

      {isSessionActive ? (
        <button
          onClick={handleStopClick}
          disabled={isStopping}
          className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-400 disabled:cursor-not-allowed transition-colors"
        >
          {isStopping ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Ending...
            </>
          ) : (
            <>
              <MicOff className="w-4 h-4 mr-2" />
              End Session
            </>
          )}
        </button>
      ) : (
        <button
          onClick={handleStartClick}
          disabled={!isSupported || isStarting}
          className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {isStarting ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <Mic className="w-4 h-4 mr-2" />
              Start Session
            </>
          )}
        </button>
      )}

      {/* Session Mode Selector Modal */}
      {showModeSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                Select Session Mode
              </h2>
              <button
                onClick={handleClose}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Choose how you want to interact during your interview session
            </p>

            {/* Mode Options */}
            <div className="space-y-4">
              {/* Chat Only Mode */}
              <button
                onClick={() => handleModeSelect('chat-only')}
                disabled={isStarting}
                className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                    <MessageSquare className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Chat Only
                    </h3>
                    <p className="text-sm text-gray-600">
                      Type your questions and get AI responses. No audio capture required.
                      Perfect for practicing responses without voice input.
                    </p>
                  </div>
                </div>
              </button>

              {/* Meet Voice and Chat Mode */}
              <button
                onClick={() => handleModeSelect('meet-voice-chat')}
                disabled={isStarting}
                className="w-full p-4 border-2 rounded-lg hover:border-primary-500 hover:bg-primary-100 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                    <Video className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Meet Voice and Chat
                      </h3>
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                        Recommended for Online Interviews
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Captures audio from your meeting tab (Google Meet, Zoom, etc.) and provides 
                      real-time transcription with AI responses. Also allows chatbot questions.
                    </p>
                  </div>
                </div>
              </button>

              {/* Mic Voice and Chat Mode */}
              <button
                onClick={() => handleModeSelect('mic-voice-chat')}
                disabled={isStarting}
                className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                    <Mic className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Mic Voice and Chat
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      Use your device microphone to speak directly. Perfect for practicing your interview 
                      responses with real-time AI feedback. Also allows chatbot questions.
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                You can change the session mode by ending and restarting your session
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
